import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Captcha from "components/captcha/Captcha";
import ContactBlockDsfr from "components/ContactBlockDsfr";
import HoneyPot from "components/HoneyPot";
import LanguageContext from "components/LanguageContext";
import SiteContext from "components/SiteContext";
import Alert from "components/templatesComponents/Alert";
import Button from "components/templatesComponents/Button";
import Icon from "components/templatesComponents/Icon";
import InputDSFR from "components/templatesComponents/InputDSFR";
import Pictogram from "components/templatesComponents/Pictogram";
import SelectDSFR from "components/templatesComponents/SelectDSFR";
import { buttonStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useContext, useMemo, useRef, useState } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import ContactService from "services/ContactService";
import EulerianService from "services/EulerianService";
import {
  checkEmailRules,
  checkFrenchNameRules,
  checkMessageLength,
  getTranslateIndex,
  immatCycloRegex,
  immatOldSivRegex,
  immatSivRegex,
  permisRegex,
  stringToSnakeCaseString,
} from "utils/commonUtils";
import t from "utils/locales/translation.json";

const ContactFormBlock = (props) => {
  const { page } = props;
  const { title, contents, id: pageId } = page;
  const { ContactFormProfilesList = {}, ContactFormSubjectsList = {}, contactForm = {} } = contents;

  const {
    title: contactFormTitle,
    subTitle: contactFormSubTitle,
    delay: contactFormDelay,
    description: contactFormDescription,
  } = contactForm;

  const site = useContext(SiteContext);
  const { language } = useContext(LanguageContext);
  const { name: siteName } = site;

  const captchaRef = useRef(null);

  const honeyKey = process.env.honey_key;

  // Contact form
  const [form, setForm] = useState({
    name: "",
    firstName: "",
    profile: "",
    email: "",
    subject: "",
    numImmat: "",
    numPermis: "",
    day: "",
    month: "",
    year: "",
    noBirthDate: false,
    message: "",
  });
  const [fileForm, setFileForm] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  // 4 states : 0 = selectSubject, 1 = auto reply, 2 = contact us, 2.1 = captcha,  3 = sent
  const [formState, setFormState] = useState(0);
  const { id: subjectId } = ContactFormSubjectsList;
  const [loader, setLoader] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [captchaBlacklisted, setCaptchaBlacklisted] = useState(false);
  const [hasCaptchaLoaded, setHasCaptchaLoaded] = useState(false);
  const [honeyPotValue, setHoneyPotValue] = useState("");

  let { ContactFormProfiles = [] } = ContactFormProfilesList;
  if (ContactFormProfiles && !Array.isArray(ContactFormProfiles)) {
    ContactFormProfiles = [ContactFormProfiles];
  }

  let { ContactFormSubjects = [] } = ContactFormSubjectsList;
  if (ContactFormSubjects && !Array.isArray(ContactFormSubjects)) {
    ContactFormSubjects = [ContactFormSubjects];
  }

  const fileType = fileForm && fileForm.type.split("/");
  const authorizedFileType = process.env.mail_allowed_attachment_type.includes(fileType && fileType[1]);

  const mailSendDesactivated = useMemo(
    () => !!(process.env.mail_send_enable !== "true" && form.subject && !form.subject.autoReply),
    [form],
  );

  const errors = useMemo(() => {
    const { name, firstName, profile, email, subject, numImmat, numPermis, day, month, year, message, noBirthDate } =
      form;

    return {
      name: !checkFrenchNameRules({ key: name, length: 100 }),
      firstName: !checkFrenchNameRules({ key: firstName, length: 40 }),
      profile: !profile,
      email: !checkEmailRules({ key: email }),
      subject: !subject,
      numImmat:
        ["IMMAT", "PHA"].includes(siteName) &&
        numImmat &&
        !(
          numImmat.trim().match(immatSivRegex) ||
          numImmat.trim().match(immatCycloRegex) ||
          numImmat.trim().match(immatOldSivRegex)
        ),
      numPermis: ["PERMIS", "AUTO_ECOLE"].includes(siteName) && numPermis && !numPermis.match(permisRegex),
      day:
        siteName === "PERMIS" && !noBirthDate && (!day || Number.isNaN(day) || day < 1 || day > 31 || day.length !== 2),
      month:
        siteName === "PERMIS" &&
        !noBirthDate &&
        (!month || Number.isNaN(month) || month < 1 || month > 12 || month.length !== 2),
      year:
        siteName === "PERMIS" &&
        (!year || Number.isNaN(year) || year < 1900 || year > new Date().getFullYear() || year.length !== 4),
      message: !checkMessageLength({ key: message }),
      fileForm: fileForm && fileForm.size && (fileForm.size >= process.env.mail_attachment_size || !authorizedFileType),
    };
  }, [form, siteName, fileForm, authorizedFileType]);

  const showError = (key) => formSubmitted && errors[key];

  const dateToSend = useMemo(() => {
    const { day, month, year, noBirthDate } = form;
    if (day && month && year) {
      return `${day}/${month}/${year}`;
    }
    if (noBirthDate && year) {
      return year;
    }
    return "";
  }, [form]);

  const handleFormChange = (e) => {
    const { name: fieldName } = e.target;
    let { value } = e.target;
    if (fieldName === "subject") {
      value = ContactFormSubjects.find((subject) => subject.title === value);
    }
    if (fieldName === "profile") {
      value = ContactFormProfiles.find((profile) => profile.title === value);
    }
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const ScrollSent = () => {
    scrollIntoView(document.getElementById("contactForm"), {
      block: "nearest",
      inline: "nearest",
    });
  };

  const isFormValid = Object.values(errors).reduce((a, b) => a && !b, true);

  const sendMail = (token, hpValue) => {
    const formData = new FormData();
    const { name, firstName, email, message, profile, subject, numImmat, numPermis } = form;
    if (fileForm) {
      formData.append("attachment", fileForm, fileForm.name);
    }
    formData.append("siteName", siteName);
    formData.append(honeyKey, hpValue || honeyPotValue);
    formData.append("name", name);
    formData.append("firstName", firstName);
    formData.append("email", email);
    formData.append("message", message);
    formData.append("profileTitle", profile && profile.title);
    formData.append("subjectTitle", subject && subject.title);
    formData.append("numImmat", numImmat.trim().toUpperCase());
    formData.append("numPermis", numPermis);
    formData.append("birthDate", dateToSend);
    formData.append("contentId", subjectId);
    formData.append("pageId", pageId);
    formData.append("subjectId", subject && subject.id);
    formData.append("tokenCaptcha", token);
    if (subject) {
      ContactService.postMail(formData)
        .then((response) => {
          if (response && response.status && (response.status === 400 || response.status === 401)) {
            setSendError(response.message);
          } else {
            setFormState(3);
          }
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
          setFormState(2);
          setSendError(t[language].help.error_server);
        });
    } else {
      setFormState(1);
      setLoader(false);
    }
  };

  const checkCaptcha = async () => {
    ScrollSent();
    if (captchaRef && captchaRef.current) {
      const tokenCode = captchaRef.current.getTokenCode();
      // 102 : blacklisted
      if (tokenCode === "102") {
        setSendError(t[language].components.captcha_blacklist_message);
        setCaptchaBlacklisted(true);
        setLoader(false);
        return;
      }
      const token = captchaRef.current.getToken();
      if (token === null) {
        setLoader(false);
        captchaRef.current.setCaptchaErrorMessage(t[language].components.captcha_error_message);
        return;
      }
      captchaRef.current.setCaptchaErrorMessage("");
      sendMail(token);
      EulerianService.sendClick({ button: [`button::action::envoyer votre message`] });
    } else {
      setLoader(false);
      captchaRef.current.setCaptchaErrorMessage(t[language].components.captcha_error_message);
    }
  };

  const handleSubmitSubject = () => {
    if (!errors.subject) {
      if (form.subject.autoReply) {
        setFormState(1);
        EulerianService.sendPage(
          {
            page_name: "affichage_reponse_automatique",
            page_category2: stringToSnakeCaseString(title),
            path: "/affichage_reponse_automatique",
            referrer: typeof document !== "undefined" ? document?.location?.pathname : "",
            page_title: "affichage_reponse_automatique",
          },
          siteName,
        );
      } else {
        setFormState(2);
        setFormSubmitted(false);
      }
    } else {
      setFormSubmitted(true);
    }
  };

  const handleUploadFile = (e) => {
    const fileToUpload = e.target.files[0];
    setFileForm(fileToUpload);
  };

  const handleBackToForm = (e) => {
    e.preventDefault(); // prevent scroll to top

    setForm({
      ...form,
      subject: "",
      message: "",
    });
    setFileForm(null);
    setFormSubmitted(false);
    setFormState(0);
    EulerianService.sendClick({ button: [`button::click::retour au formulaire`] });
  };

  const handleContactUs = () => {
    setFormSubmitted(false);
    setFormState(2);
  };

  const handleValidateForm = () => {
    setFormSubmitted(true);
    if (isFormValid) {
      const hpValue = document.querySelector(`#${honeyKey}`)?.value;
      setLoader(true);
      if (!process.env.liveidentity_key && !process.env.liveidentity_key) {
        sendMail(undefined, hpValue);
      } else {
        setHoneyPotValue(hpValue);
        ScrollSent();
        setFormState(2.1);
        setLoader(false);
      }
    }
  };

  const errorFileMessage = useMemo(() => {
    const messages = [];
    if (fileForm && fileForm.size >= process.env.mail_attachment_size) {
      messages.push(t[language].help.attachment_error_message);
    }
    if (fileForm && fileForm.type && !authorizedFileType) {
      messages.push(t[language].help.attachment_unauthorized_message);
    }
    return messages;
  }, [fileForm, authorizedFileType, language]);

  return (
    <ContactBlockDsfr
      leftChild={
        <Stack spacing={3}>
          <h3 id="contactForm">{contactFormTitle}</h3>
          <div className="fr-text--lg fr-text--bold">{contactFormSubTitle}</div>
          <Stack direction="row" spacing={1.25} sx={{ "& .fr-text--lg": { flex: 1 } }}>
            <Icon iconDSFR="time-line" title={t[language].help.deadline} />
            <div className="fr-text--lg">{contactFormDelay}</div>
          </Stack>
          <div className="fr-text--lg">{contactFormDescription}</div>
        </Stack>
      }
      rightChild={
        <>
          <Box
            component="form"
            action="/"
            method="POST"
            onSubmit={(e) => {
              e.preventDefault();
              handleValidateForm();
            }}
          >
            {loader && (
              <Grid
                container
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  bgcolor: "#eff4fd",
                  opacity: "0.5",
                  zIndex: 1000,
                  "& svg": {
                    color: "secondary.main",
                  },
                }}
              >
                <CircularProgress />
              </Grid>
            )}
            <Stack spacing={4} sx={{ "& hr": { p: 0, pt: 0.125 } }}>
              {formState === 0 && (
                <>
                  <div className={`fr-select-group${showError("subject") ? " fr-select-group--error" : ""} fr-mb-0`}>
                    <label className="fr-label" htmlFor="select-subject">
                      {t[language].help.object}
                    </label>
                    <SelectDSFR
                      name="subject"
                      id="select-subject"
                      value={form.subject?.title}
                      onChange={handleFormChange}
                      error={showError("subject")}
                      errorMessage={t[language].common.required_fields[getTranslateIndex(1)]}
                      Wrapper={ContactFormSubjectsList.Wrapper}
                      wrapperProps={ContactFormSubjectsList.wrapperProps}
                    >
                      <option value="" selected disabled>
                        {t[language].help.object_placeholder}
                      </option>
                      {ContactFormSubjects.map(
                        (subject) =>
                          subject.title &&
                          (subject.recipient || subject.autoReply) && (
                            <option value={subject.title} key={subject.id}>
                              {subject.title}
                            </option>
                          ),
                      )}
                    </SelectDSFR>
                  </div>
                  <Button disabled={mailSendDesactivated} onClick={handleSubmitSubject} sx={buttonStyled}>
                    {t[language].components.validate_button_label}
                  </Button>
                </>
              )}

              {[1, 2].includes(formState) && (
                <>
                  <Stack spacing={1.5} sx={{ "& > a": { width: "fit-content" } }}>
                    <p>
                      <b>{t[language].help.subject} :</b> {form.subject?.title}
                    </p>
                    <a
                      id="back-to-subject"
                      href="#"
                      onClick={handleBackToForm}
                      className="fr-link fr-link--sm fr-icon-arrow-left-line fr-link--icon-left"
                    >
                      {t[language].help.change_subject}
                    </a>
                  </Stack>
                  <hr />
                </>
              )}

              {formState === 1 && (
                <>
                  <Stack spacing={2.5}>
                    <h5>{t[language].help.auto_reply_title}</h5>
                    <p>{t[language].help.auto_reply_body1},</p>
                    {form.subject.replyMessage}
                  </Stack>
                  {form.subject.canContact && form.subject.recipient && (
                    <>
                      <hr />
                      <Stack spacing={2}>
                        <p>{t[language].help.auto_reply_body2}</p>
                        <Button onClick={handleContactUs} sx={buttonStyled}>
                          {t[language].help.contact_button_label}
                        </Button>
                      </Stack>
                    </>
                  )}
                </>
              )}

              {formState === 2 && process.env.mail_send_enable === "true" && (
                <>
                  <p className="fr-text--md fr-hint-text fr-mt-2w">{t[language].common.required_fields_mention}</p>
                  <Stack spacing={1}>
                    <InputDSFR
                      label={`${t[language].help.first_name}`}
                      name="firstName"
                      value={form.firstName}
                      onChange={handleFormChange}
                      autoComplete="given-name"
                      error={showError("firstName")}
                      errorMessage={
                        form.firstName.length > 40
                          ? `40 ${t[language].help.max_characters}`
                          : t[language].help.first_name_error_message
                      }
                    />
                    <InputDSFR
                      label={`${t[language].help.name}`}
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      autoComplete="family-name"
                      error={showError("name")}
                      errorMessage={
                        form.name.length > 100
                          ? `100 ${t[language].help.max_characters}`
                          : t[language].help.name_error_message
                      }
                    />
                    <div className={`fr-select-group${showError("profile") ? " fr-select-group--error" : ""}`}>
                      <label className="fr-label" htmlFor="select-profile">
                        {t[language].help.profil_label}
                      </label>
                      <SelectDSFR
                        name="profile"
                        id="select-profile"
                        value={form.profile?.title}
                        onChange={handleFormChange}
                        error={showError("profile")}
                        errorMessage={t[language].common.required_fields[getTranslateIndex(1)]}
                        Wrapper={ContactFormProfilesList.Wrapper}
                        wrapperProps={ContactFormProfilesList.wrapperProps}
                      >
                        <option value="" selected disabled>
                          {t[language].help.profil_placeholder}
                        </option>
                        {ContactFormProfiles.map(
                          (profile) =>
                            profile.title && (
                              <option value={profile.title} key={profile.id}>
                                {profile.title}
                              </option>
                            ),
                        )}
                      </SelectDSFR>
                    </div>
                    <InputDSFR
                      label={`${t[language].help.mail}`}
                      hint={`${t[language].help.expected_format} ${t[language].help.expected_mail_format}`}
                      name="email"
                      value={form.email}
                      onChange={handleFormChange}
                      type="email"
                      autoComplete="email"
                      error={showError("email")}
                      errorMessage={
                        form.email.length > 80
                          ? `80 ${t[language].help.max_characters}`
                          : t[language].help.mail_error_message
                      }
                    />
                    {["IMMAT", "PHA"].includes(siteName) && (
                      <InputDSFR
                        label={`${t[language].help.immat_number} (${t[language].help.optional})`}
                        hint={`${t[language].help.expected_format_example} ${t[language].help.expected_immat_format}`}
                        name="numImmat"
                        value={form.numImmat}
                        onChange={handleFormChange}
                        error={showError("numImmat")}
                        errorMessage={t[language].help.immat_number_error_message}
                        sx={form.numImmat ? { textTransform: "uppercase" } : {}}
                      />
                    )}
                    {["PERMIS", "AUTO_ECOLE"].includes(siteName) && (
                      <InputDSFR
                        label={`${t[language].help.license_number} (${t[language].help.optional})`}
                        hint={`${t[language].help.expected_format} ${t[language].help.expected_license_format}`}
                        name="numPermis"
                        value={form.numPermis}
                        onChange={handleFormChange}
                        error={showError("numPermis")}
                        errorMessage={t[language].help.license_number_error_message}
                      />
                    )}
                    {siteName === "PERMIS" && (
                      <fieldset
                        className="fr-fieldset"
                        id="birthdate-fieldset"
                        role="group"
                        aria-labelledby="birthdate-fieldset-legend birthdate-fieldset-fieldset-messages"
                      >
                        <legend className="fr-fieldset__legend" id="date-default-1578-fieldset-legend">
                          {t[language].help.birth_date}
                        </legend>
                        <Stack direction="row" spacing={2}>
                          <InputDSFR
                            label={t[language].help.day}
                            hint={`${t[language].help.expected_format} 14`}
                            name="day"
                            value={form.day}
                            onChange={handleFormChange}
                            error={showError("day")}
                            min={1}
                            max={31}
                          />
                          <InputDSFR
                            label={t[language].help.month}
                            hint={`${t[language].help.expected_format} 07`}
                            name="month"
                            value={form.month}
                            onChange={handleFormChange}
                            error={showError("month")}
                            min={1}
                            max={12}
                          />
                          <InputDSFR
                            label={t[language].help.year}
                            hint={`${t[language].help.expected_format} 1989`}
                            name="year"
                            value={form.year}
                            onChange={handleFormChange}
                            error={showError("year")}
                            min={1900}
                            max={new Date().getFullYear()}
                            className="fr-mb-3w"
                          />
                        </Stack>
                        <div className="fr-checkbox-group fr-checkbox-group--sm fr-mb-4w fr-mt-n1w">
                          <input
                            name="noBirthDate"
                            id="checkbox-birthdate"
                            type="checkbox"
                            checked={form.noBirthDate}
                            aria-describedby="no-birthdate-checkbox"
                            onChange={() => setForm({ ...form, noBirthDate: !form.noBirthDate })}
                          />
                          <label className="fr-label" htmlFor="checkbox-birthdate">
                            {t[language].help.no_birth_date}
                          </label>
                        </div>
                        {(showError("day") || showError("month") || showError("year")) && (
                          <div className="fr-messages-group" id="input-birthDate-message" aria-live="polite">
                            <p className="fr-message fr-message--error" id="input-birthDate-error">
                              {form.noBirthDate
                                ? t[language].help.year_birth_date_error_message
                                : t[language].help.birth_date_error_message}
                            </p>
                          </div>
                        )}
                      </fieldset>
                    )}
                    <InputDSFR
                      rows={6}
                      label={t[language].help.message}
                      hint={`${t[language].help.expected_format} ${t[language].help.expected_message_format}`}
                      name="message"
                      value={form.message}
                      onChange={handleFormChange}
                      error={showError("message")}
                      errorMessage={
                        form.message.length > 2000
                          ? `2000 ${t[language].help.max_characters}`
                          : t[language].common.required_fields[getTranslateIndex(1)]
                      }
                    />
                    <Box className="fr-mb-3w">
                      <InputDSFR
                        label={t[language].help.attachment}
                        hint={t[language].help.attachment_hint}
                        name="file-attachment"
                        type="file"
                        onChange={handleUploadFile}
                        accept={process.env.mail_allowed_attachment_type}
                        error={showError("fileForm")}
                        errorMessage={errorFileMessage}
                      />
                    </Box>
                    <HoneyPot name={honeyKey} />
                    <Button disabled={captchaBlacklisted || mailSendDesactivated} sx={buttonStyled}>
                      {t[language].help.button_label}
                    </Button>
                    {sendError && <span className="fr-message fr-message--error">{sendError}</span>}
                  </Stack>
                </>
              )}
            </Stack>
          </Box>

          {formState === 2.1 && (
            <Box>
              <h6>Pour valider l’envoi de votre message, nous devons vérifier que vous n’êtes pas un robot</h6>
              {!hasCaptchaLoaded && (
                <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                  <CircularProgress />
                </Grid>
              )}
              {formState !== 3 && (
                <>
                  <Captcha
                    ref={captchaRef}
                    id="sample-captcha"
                    onCaptchaLoaded={() => {
                      setHasCaptchaLoaded(true);
                    }}
                    onCaptchaValidate={checkCaptcha}
                  />
                  {captchaBlacklisted && (
                    <Alert type="error">
                      <p>
                        Pour des raisons de sécurité, votre accès à cette fonctionnalité a été restreint. Veuillez
                        ré-essayer plus tard.
                      </p>
                    </Alert>
                  )}
                </>
              )}
            </Box>
          )}

          {mailSendDesactivated && (
            <Box sx={{ position: "absolute", bottom: 8 }}>
              <span className="fr-message fr-message--error">{t[language].help.send_mail_error_message}</span>
            </Box>
          )}

          {formState === 3 && (
            <Stack spacing={4} sx={{ "& svg": { width: "120px", alignSelf: "center" } }}>
              <Pictogram folder="digital" pictogram="mail-send" />
              <Stack spacing={3} sx={{ "& hr": { p: 0, pt: 0.125 } }}>
                <h3>{t[language].help.message_sent_subtitle}</h3>
                <p>{t[language].help.message_sent_description}</p>
                <hr />
              </Stack>
              <Button kind="secondary" onClick={handleBackToForm} sx={buttonStyled}>
                {t[language].help.back_button_label}
                <Icon iconDSFR="arrow-go-back-line" size="sm" aria-hidden sx={{ pl: 1 }} />
              </Button>
            </Stack>
          )}
        </>
      }
    />
  );
};

ContactFormBlock.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default ContactFormBlock;
